import { ChargingAPI_BASE_URL, WHSSubsAPI_BASE_URL, PROMOTION_API_BASE_URL, JAZZ_WEBDOC_DETAILS_API_BASE_URL, UFONE_SUB_DETAILS_API_BASE_URL, TABEEB_API_BASE_URL, API_BASE_URL } from "../config/apiConfig";

// ─────────────────────────────────────────────────────────────────
// Centralised API layer — all networks handled here.
//
//  6by6 networks (same response shape, different path):
//    Zong     → /api/zong/sub_dump/{msisdn}   (superseded by Promotion API for Zong)
//
//  Jazz:
//    → /api/jazz/subDetails?msisdn={03XXXXXXXXX}
//
//  Telenor:
//    → /api/telenor/subDetails?cellno={03XXXXXXXXX}
//    → /api/telenor/unSubscribe?dbname={dbname}&cellno={03XXXXXXXXX}&unSubMode=SMS
//
//  Ufone:
//    → /api/ufone/subDetails?cellno={03XXXXXXXXX}
//
//  WHS / Easypaisa:
//    → CheckWHSSubscriptions?msisdn={92XXXXXXXXXX}
//
//  msisdn formats:
//    6by6     → 10-digit  e.g. 3175777308
//    Jazz     → 11-digit local e.g. 03035507000
//    Telenor  → 11-digit local e.g. 03487878134
//    Ufone    → 11-digit local e.g. 03035507000
//    WHS      → 12-digit  e.g. 923088305555
// ─────────────────────────────────────────────────────────────────

// ── Phone format helpers ──────────────────────────────────────────

/** 3XXXXXXXXX — 10 digits, no 92, no leading 0 */
const to10Digit = (phone) => {
  const d = phone.replace(/\D/g, "");
  if (d.startsWith("92") && d.length === 12) return d.slice(2);
  if (d.startsWith("0")  && d.length === 11) return d.slice(1);
  return d;
};

const to11DigitLocal = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.startsWith("92") && d.length === 12) return `0${d.slice(2)}`;
  if (d.startsWith("0") && d.length === 11) return d;
  if (d.length === 10) return `0${d}`;
  return d;
};

/** 92XXXXXXXXXX — 12 digits with country code */
const to12Digit = (phone) => {
  const d = phone.replace(/\D/g, "");
  if (d.startsWith("92") && d.length === 12) return d;
  if (d.startsWith("0")  && d.length === 11) return "92" + d.slice(1);
  if (d.length === 10)                        return "92" + d;
  return d;
};

// ── Product meta map ──────────────────────────────────────────────
const PRODUCT_META = {
  RSA_BIKE:            { name: "RSA Bike Protection",   icon: "🏍️", type: "Road Side Assistance", color: "#e67e22", pricePerMonth: 49  },
  RSA_CAR:             { name: "RSA Car Protection",    icon: "🚗", type: "Road Side Assistance", color: "#0099CC", pricePerMonth: 99  },
  MOBILE_INSURANCE:    { name: "Mobile Insurance",      icon: "📱", type: "Insurance",            color: "#9b59b6", pricePerMonth: 29  },
  HEALTH_INSURANCE:    { name: "Health Insurance",      icon: "❤️",  type: "Insurance",           color: "#e74c3c", pricePerMonth: 149 },
  HEALTH_LEGALBUNDLE:  { name: "Health & Legal Bundle", icon: "⚖️", type: "Bundle",               color: "#27ae60", pricePerMonth: 199 },
  LIVESTOCK_INSURANCE: { name: "Livestock Insurance",   icon: "🐄", type: "Insurance",            color: "#8e6b3e", pricePerMonth: 79  },
  HEALTH_ADVISORY:     { name: "Health Advisory",       icon: "🩺", type: "Advisory",             color: "#16a085", pricePerMonth: 39  },
  KHUSHAAL_ZINDAGI:    { name: "Khushaal Zindagi",      icon: "🌟", type: "Insurance",             color: "#0067b1", pricePerMonth: 29  },
};

const PROMOTION_SERVICE_META = {
  "1": { name: "Mobile Insurance", icon: "📱", type: "Insurance", color: "#9b59b6", pricePerMonth: 29 },
  "3": { name: "RSA Bike Protection", icon: "🏍️", type: "Road Side Assistance", color: "#e67e22", pricePerMonth: 49 },
};

// ── Telenor product meta map ───────────────────────────────────────
// Keyed by the `product` value returned in the subDetails response
// (e.g. "khushal_muhafiz"). Add more entries here as new Telenor
// products go live.
const TELENOR_PRODUCT_META = {
  khushal_muhafiz: { name: "Khushal Muhafiz", icon: "🛡️", type: "Insurance", color: "#0067b1" },
};

const resolveTelenorProductMeta = (productKey) => {
  const key = String(productKey || "").trim().toLowerCase();
  if (TELENOR_PRODUCT_META[key]) return TELENOR_PRODUCT_META[key];

  const titled = key
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  return {
    name: titled || "Telenor Service",
    icon: "📱",
    type: "Service",
    color: "#0067b1",
  };
};

// ── Jazz keyword-based icon/label resolver ────────────────────────
// Naam mai kahin bhi keyword mile (crop_insurance, Crop Protection, WEBDOC-CROP-INS waghera)
// to wahi fixed icon/label/color lagega — hardcoded exact-match list pe depend nahi karta.
const JAZZ_KEYWORD_META = [
  { keys: ["crop"],                label: "Crop Insurance",      icon: "🌾", type: "Insurance", color: "#8e6b3e" },
  { keys: ["home", "content"],     label: "Home Insurance",      icon: "🏠", type: "Insurance", color: "#0067b1" },
  { keys: ["bike", "motorcycle"],  label: "Bike Insurance",      icon: "🏍️", type: "Insurance", color: "#e67e22" },
  { keys: ["car", "vehicle"],      label: "Car Insurance",       icon: "🚗", type: "Insurance", color: "#0099CC" },
  { keys: ["mobile", "device"],    label: "Mobile Insurance",    icon: "📱", type: "Insurance", color: "#9b59b6" },
  { keys: ["health"],              label: "Health Insurance",    icon: "❤️", type: "Insurance", color: "#e74c3c" },
  { keys: ["livestock", "cattle"], label: "Livestock Insurance", icon: "🐄", type: "Insurance", color: "#8e6b3e" },
  { keys: ["legal"],               label: "Legal Bundle",        icon: "⚖️", type: "Insurance", color: "#27ae60" },
];

const resolveJazzServiceMeta = (rawServiceName) => {
  const normalized = String(rawServiceName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " "); // "crop_insurance" / "WEBDOC-CROP-INS" → "crop insurance"

  const matched = JAZZ_KEYWORD_META.find((entry) =>
    entry.keys.some((keyword) => normalized.includes(keyword))
  );

  if (matched) {
    return {
      label: `Webdoc ${matched.label}`,
      icon: matched.icon,
      type: matched.type,
      color: matched.color,
    };
  }

  // Fallback: koi keyword match nahi hua to naam ko titlecase kar k generic icon
  const titled = normalized
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  return {
    label: `Webdoc ${titled || rawServiceName}`,
    icon: "🛡️",
    type: "Insurance",
    color: "#888",
  };
};

const formatDate = (value) => {
  if (!value) return "—";
  const normalized = String(value).trim().replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-PK");
};

const toPromotionMsisdn = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.startsWith("92") && d.length === 12) return d;
  if (d.startsWith("0") && d.length === 11) return d;
  if (d.length === 10) return `0${d}`;
  return d;
};

// ── Normalisers ───────────────────────────────────────────────────

/** Used by all 6by6 APIs — same response shape */
const normalise6by6 = (raw) => {
  const meta = PRODUCT_META[raw.product] || {
    name: raw.product, icon: "🛡️", type: "Service", color: "#888", pricePerMonth: 0,
  };
  return {
    id:            raw.policy,
    name:          meta.name,
    icon:          meta.icon,
    type:          meta.type,
    color:         meta.color,
    status:        "Active",
    subscribedOn:  new Date(raw.sub_date).toLocaleDateString("en-PK"),
    pricePerMonth: meta.pricePerMonth,
    totalDeducted: raw.last_charge_date ? meta.pricePerMonth * 3 : 0,
    maxCoverage:   `Rs. ${meta.pricePerMonth * 150}`,
    subMode:       raw.sub_mode,
    lastCharged:   raw.last_charge_date
                     ? new Date(raw.last_charge_date).toLocaleDateString("en-PK")
                     : "—",
    product:       raw.product,
  };
};

/** Used by Easypaisa / WHS API */
const normaliseEasypaisa = (raw) => ({
  id:            String(raw.id),
  name:          raw.service || "WHS Service",
  icon:          "💚",
  type:          "Easypaisa Service",
  color:         "#00a86b",
  status:        raw.status || "Active",
  subscribedOn:  new Date(raw.date).toLocaleDateString("en-PK"),
  pricePerMonth: raw.amount,
  totalDeducted: raw.amount,
  maxCoverage:   "—",
  subMode:       "Easypaisa",
  lastCharged:   raw.monthYear || "—",
  product:       raw.service,
  holderName:    raw.name,
});

const normalisePromotionSubscription = (raw, sourceKey) => {
  const serviceId = String(raw.service_id || "").trim();
  const meta = PROMOTION_SERVICE_META[serviceId] || {
    name: raw.service_name || raw.op || `Service ${serviceId || "Unknown"}`,
    icon: "🛡️",
    type: "Service",
    color: "#888",
    pricePerMonth: 0,
  };

  return {
    id: `${serviceId || "unknown"}-${raw.cellno || ""}-${raw.sub_dt || ""}`,
    name: meta.name,
    icon: meta.icon,
    type: meta.type,
    color: meta.color,
    status: String(raw.status || "").trim() === "100" ? "Active" : "Inactive",
    subscribedOn: formatDate(raw.sub_dt),
    pricePerMonth: meta.pricePerMonth,
    totalDeducted: raw.last_charge_dt ? meta.pricePerMonth : 0,
    maxCoverage: meta.pricePerMonth ? `Rs. ${meta.pricePerMonth * 150}` : "—",
    subMode: raw.sub_mode || "—",
    lastCharged: formatDate(raw.last_charge_dt),
    product: meta.name,
    holderName: raw.cellno || "—",
    apiService: sourceKey,
    apiServiceLabel: raw.apiServiceLabel || sourceKey || "Promotion Service",
  };
};

// ── Jazz subDetails normaliser ─────────────────────────────────────
// Raw response shape:
// { serviceId, serviceName, productCode, channel, subscriptionDate, nextRenewalDate }
const normaliseJazzSubDetail = (raw) => {
  const meta = resolveJazzServiceMeta(raw.serviceName);

  return {
    id: `jazz-${raw.serviceId || "unknown"}-${raw.productCode || ""}`,
    name: meta.label,
    icon: meta.icon,
    type: meta.type,
    color: meta.color,
    status: "Active",
    subscribedOn: formatDate(raw.subscriptionDate),
    pricePerMonth: null,        // Jazz API price nahi deta — UI mai hidden rahega
    totalDeducted: 0,
    maxCoverage: "—",
    subMode: raw.channel || "Jazz",
    lastCharged: "—",
    nextRenewalDate: formatDate(raw.nextRenewalDate),
    product: meta.label,
    holderName: "—",
    apiService: "Jazz",
    apiServiceLabel: "Jazz Webdoc",
    // Cancel/unsubscribe call k liye zaroori raw fields:
    productCode: raw.productCode,
    serviceNameRaw: raw.serviceName,
    channel: raw.channel || "Digital",
  };
};

// ── Generic 6by6 fetcher (kept for any remaining 6by6-style networks) ──

const fetch6by6 = async (networkPath, phone, label) => {
  const msisdn = to10Digit(phone);
  console.log(`[${label}] msisdn sent → ${msisdn}`);

  const res = await fetch(
    `${ChargingAPI_BASE_URL}/${networkPath}/sub_dump/${msisdn}`
  );
  if (!res.ok) throw new Error(`${label} API error: ${res.status}`);

  const json = await res.json();
  const records = Array.isArray(json) ? json : (json.ResponseData || []);

  if (records.length === 0) return [];

  // Deduplicate — keep latest sub_datetime per product
  const latest = {};
  for (const r of records) {
    if (
      !latest[r.product] ||
      new Date(r.sub_datetime) > new Date(latest[r.product].sub_datetime)
    ) {
      latest[r.product] = r;
    }
  }

  return Object.values(latest).map(normalise6by6);
};

// ── New Zong promotion API fetcher ───────────────────────────────

const fetchZongPromotionSubscriptions = async (phone) => {
  const msisdn = toPromotionMsisdn(phone);
  const url = `${PROMOTION_API_BASE_URL}/get-all-subscription`;
  console.log(`[Zong Promotion] request → ${url} msisdn → ${msisdn}`);

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ msisdn }),
    });

    const text = await res.text();
    let json = null;

    if (text) {
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.warn("[Zong Promotion] response was not JSON", text);
      }
    }

    if (!res.ok) {
      console.warn(`[Zong Promotion] API returned ${res.status}`, json || text);
      return [];
    }

    if (!json || String(json.statusCode || "") === "0001" || json.count === 0 || !json.payLoad) {
      return [];
    }

    const payload = json.payLoad || {};
    const records = [];

    ["API_HBS", "API_MIS", "API_HIS", "API_IP"].forEach((key) => {
      if (Array.isArray(payload[key])) {
        payload[key].forEach((record) => records.push({ ...record, __sourceKey: key, apiServiceLabel: record.apiServiceLabel || key }));
      }
    });

    if (records.length === 0) return [];

    const latestByKey = {};
    for (const record of records) {
      const key = `${record.__sourceKey || "unknown"}-${record.service_id || "unknown"}-${record.sub_dt || ""}-${record.op || ""}`;
      if (!latestByKey[key]) latestByKey[key] = record;
    }

    return Object.values(latestByKey).map((record) => normalisePromotionSubscription(record, record.__sourceKey));
  } catch (error) {
    console.warn("[Zong Promotion] request failed", error);
    return [];
  }
};

// ── Jazz subDetails fetcher ───────────────────────────────────────

const fetchJazzSubDetails = async (phone) => {
  const msisdn = to11DigitLocal(phone); // e.g. 03035507000
  const url = `${JAZZ_WEBDOC_DETAILS_API_BASE_URL}/subDetails?msisdn=${msisdn}`;
  console.log(`[Jazz] request → ${url}`);

  try {
    const res = await fetch(url);
    const text = await res.text();
    let json = null;

    if (text) {
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.warn("[Jazz] response was not JSON", text);
      }
    }

    if (!res.ok || !json || !json.success) {
      console.warn(`[Jazz] API returned ${res.status}`, json || text);
      return [];
    }

    const records = Array.isArray(json.data) ? json.data : [];
    return records.map(normaliseJazzSubDetail);
  } catch (error) {
    console.warn("[Jazz] request failed", error);
    return [];
  }
};

const normaliseUfoneSubscription = (raw, msisdn) => {
  const product = String(raw.product || raw.serviceName || raw.service || "Ufone Service").trim();
  const meta = PRODUCT_META[product] || {
    name: product || "Ufone Service",
    icon: "📱",
    type: "Service",
    color: "#888",
    pricePerMonth: 0,
  };

  const isActive = String(raw.IS_SUB || raw.HAS_SERVICE || "").trim() === "Y";
  const hasGrace = String(raw.HAS_GRACE || "").trim() === "Y";
  const status = isActive ? "Active" : (hasGrace ? "Grace Period" : "Inactive");

  return {
    id: `ufone-${raw.SERVICE_ID || raw.serviceId || "unknown"}-${raw.LAST_SUB_DT || ""}`,
    name: meta.name,
    icon: meta.icon,
    type: meta.type,
    color: meta.color,
    status,
    subscribedOn: formatDate(raw.LAST_SUB_DT),
    pricePerMonth: meta.pricePerMonth,
    totalDeducted: 0,
    maxCoverage: meta.pricePerMonth ? `Rs. ${meta.pricePerMonth * 150}` : "—",
    subMode: "Ufone",
    lastCharged: "—",
    product,
    holderName: msisdn,
    apiService: "Ufone",
    apiServiceLabel: "Ufone Details",
  };
};

const fetchUfoneSubscriptions = async (phone) => {
  const msisdn = to11DigitLocal(phone);
  const url = `${API_BASE_URL}/ufone/subDetails?cellno=${msisdn}`;
  console.log(`[Ufone] request → ${url}`);

  try {
    const res = await fetch(url);
    const text = await res.text();
    let json = null;

    if (text) {
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.warn("[Ufone] response was not JSON", text);
      }
    }

    if (!res.ok || !json) {
      console.warn(`[Ufone] API returned ${res.status}`, json || text);
      return [];
    }

    if (!json.success || !json.data || String(json.data.responseCode || "") !== "100") {
      return [];
    }

    const data = json.data || {};
    const hasActiveSubscription = String(data.IS_SUB || data.HAS_SERVICE || "").trim() === "Y" || String(data.HAS_GRACE || "").trim() === "Y";

    if (!hasActiveSubscription) {
      return [];
    }

    return [normaliseUfoneSubscription({ ...data, product: json.product || data.product || data.serviceName || "Ufone Service" }, msisdn)];
  } catch (error) {
    console.warn("[Ufone] request failed", error);
    return [];
  }
};

// ── Telenor subDetails normaliser + fetcher ────────────────────────
// Raw response shape (same family as Ufone):
// {
//   success, source, product,
//   data: { responseCode, HAS_SERVICE, HAS_GRACE, LAST_SUB_DT, IS_SUB,
//           LCR_STATUS, message, SERVICE_ID, PRICE_POINT }
// }
const normaliseTelenorSubscription = (raw, msisdn, productKey) => {
  const meta = resolveTelenorProductMeta(productKey);

  const isActive = String(raw.IS_SUB || raw.HAS_SERVICE || "").trim() === "Y";
  const hasGrace = String(raw.HAS_GRACE || "").trim() === "Y";
  const status = isActive ? "Active" : (hasGrace ? "Grace Period" : "Inactive");

  return {
    id: `telenor-${raw.SERVICE_ID || "unknown"}-${raw.LAST_SUB_DT || ""}`,
    name: meta.name,
    icon: meta.icon,
    type: meta.type,
    color: meta.color,
    status,
    subscribedOn: formatDate(raw.LAST_SUB_DT),
    pricePerMonth: Number(raw.PRICE_POINT) || 0,
    totalDeducted: 0,
    maxCoverage: "—",
    subMode: "Telenor",
    lastCharged: "—",
    product: productKey,
    holderName: msisdn,
    apiService: "Telenor",
    apiServiceLabel: "Telenor Details",
    // Unsubscribe call k liye zaroori — dbname product key se derive hota hai.
    dbname: productKey,
  };
};

const fetchTelenorSubscriptions = async (phone) => {
  const msisdn = to11DigitLocal(phone); // e.g. 03487878134
  const url = `${API_BASE_URL}/telenor/subDetails?cellno=${msisdn}`;
  console.log(`[Telenor] request → ${url}`);

  try {
    const res = await fetch(url);
    const text = await res.text();
    let json = null;

    if (text) {
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.warn("[Telenor] response was not JSON", text);
      }
    }

    if (!res.ok || !json) {
      console.warn(`[Telenor] API returned ${res.status}`, json || text);
      return [];
    }

    if (!json.success || !json.data || String(json.data.responseCode || "") !== "100") {
      return [];
    }

    const data = json.data || {};
    const hasActiveSubscription = String(data.IS_SUB || data.HAS_SERVICE || "").trim() === "Y" || String(data.HAS_GRACE || "").trim() === "Y";

    if (!hasActiveSubscription) {
      return [];
    }

    const productKey = json.product || json.source || "khushal_muhafiz";
    return [normaliseTelenorSubscription(data, msisdn, productKey)];
  } catch (error) {
    console.warn("[Telenor] request failed", error);
    return [];
  }
};

// ── Easypaisa / WHS fetcher ───────────────────────────────────────

const fetchEasypaisa = async (phone) => {
  const msisdn = to12Digit(phone);
  console.log(`[Easypaisa/WHS] msisdn sent → ${msisdn}`);

  const res = await fetch(
    `${WHSSubsAPI_BASE_URL}CheckWHSSubscriptions?msisdn=${msisdn}&status=Active`
  );

  const json = await res.json().catch(() => null);

  // 404 / responseCode 0001 "No record found." → treat as no policy (empty list)
  if (
    String(json?.responseCode || "") === "0001" ||
    /no record found/i.test(json?.message || "")
  ) {
    return [];
  }

  if (!res.ok) {
    throw new Error(json?.message || `Easypaisa API error: ${res.status}`);
  }

  const records = json?.records || [];
  if (records.length === 0) return [];

  return records.map(normaliseEasypaisa);
};

// ── Tabeeb (Healthline Bundle) helpers ────────────────────────────

const normaliseTabeebSubscription = (raw, msisdn) => ({
  id:            `tabeeb-${raw.SERVICE_ID}-${raw.LAST_SUB_DT}`,
  name:          "Healthline Bundle",
  icon:          "🩺",
  type:          "Health Service",
  color:         "#3498db",
  status:        raw.HAS_SERVICE === "Y" ? "Active" : (raw.HAS_GRACE === "Y" ? "Grace Period" : "Inactive"),
  subscribedOn:  formatDate(raw.LAST_SUB_DT),
  pricePerMonth: Number(raw.PRICE_POINT) || 0,
  totalDeducted: 0,
  maxCoverage:   "—",
  subMode:       raw.SUB_MODE || "Tabeeb",
  lastCharged:   formatDate(raw.NEXT_CHARGE_DT),
  product:       "HealthlineBundle",
  holderName:    msisdn,
  apiService:    "Tabeeb",
  apiServiceLabel: "Tabeeb Healthline",
});

const fetchTabeebSubscriptions = async (phone) => {
  const msisdn = to11DigitLocal(phone);
  const url = `${TABEEB_API_BASE_URL}/Zong/HealthlineBundle/checkSub?msisdn=${msisdn}`;
  console.log(`[Tabeeb] request → ${url}`);

  try {
    const res = await fetch(url);
    const text = await res.text();
    let json = null;

    if (text) {
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.warn("[Tabeeb] response was not JSON", text);
      }
    }

    if (!res.ok || !json) {
      console.warn(`[Tabeeb] API returned ${res.status}`, json || text);
      return [];
    }

    if (String(json.responseCode) !== "100" || json.IS_SUB !== "Y") {
      return [];
    }

    return [normaliseTabeebSubscription(json, msisdn)];
  } catch (error) {
    console.warn("[Tabeeb] request failed", error);
    return [];
  }
};

// ── Main export ───────────────────────────────────────────────────
/**
 * fetchSubscriptions(phone, network)
 *
 * phone   — raw user input: "031...", "92...", or "3..."
 * network — dropdown value from Home form
 *
 * Returns: Promise<normalisedService[]>
 *   → empty []  if API returns no records
 *   → throws    on network/server error (caught by useSubscriptions)
 *
 * Network → API mapping:
 *   Jazz      → subDetails (new Jazz API)
 *   Zong      → Promotion get-all-subscription
 *   Telenor   → Telenor subDetails
 *   Ufone     → Ufone subDetails
 *   Easypaisa → WHS CheckWHSSubscriptions
 *   Tabeeb    → Tabeeb checkSub
 *   Warid     → no API yet → []
 *   JazzCash  → no API yet → []
 */
export const fetchSubscriptions = async (phone, network) => {
  const net = (network || "").toLowerCase().trim();

  if (net === "zong")      return fetchZongPromotionSubscriptions(phone);
  if (net === "jazz")      return fetchJazzSubDetails(phone);
  if (net === "telenor")   return fetchTelenorSubscriptions(phone);
  if (net === "ufone")     return fetchUfoneSubscriptions(phone);
  if (net === "easypaisa") return fetchEasypaisa(phone);
  if (net === "tabeeb")    return fetchTabeebSubscriptions(phone);
  // Warid / JazzCash — no API available yet
  console.warn(`[subscriptionApi] No API for network: ${network}`);
  return [];
};